import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Dialog,
  DialogContent,
  IconButton,
  CircularProgress,
  useMediaQuery,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { ODDS_HISTORY_API_URL } from '../config';
import { useOddsFormat, fmtPrice } from '../oddsFormat';

// Same host as odds-history; alternates aren't ingested, so this one hits the server.
const ALT_ODDS_API_URL = ODDS_HISTORY_API_URL.replace(/odds-history\/?$/, 'alt-odds');

// ══════════════════════════════════════════════════════════
//  GROUPING — works off the bets snapshot the client already holds.
//  Pass playerId to get one player's props; omit it for a game's
//  non-player markets (spreads, totals, team totals, moneylines).
// ══════════════════════════════════════════════════════════

// Display order for the side columns; anything unknown falls in after these.
// Pick'em / DFS-style books: fixed-payout multipliers rather than true two-way
// prices, so they distort "best price". Excluded from this modal only.
const PICKEM_BOOK_RE = /prizepicks|underdog|pick\s?6|sleeper|props\s?builder|dabble|betr\b|chalkboard|parlayplay/i;
export const isPickemBook = (name) => PICKEM_BOOK_RE.test(String(name || ''));

const SIDE_ORDER = ['Over', 'Under', 'Away', 'Home', 'Draw', 'Yes', 'No'];
// Sides that belong to the whole game rather than to one team.
const NEUTRAL_SIDES = new Set(['over', 'under', 'yes', 'no']);
const sameTeam = (a, b) => !!a && !!b && String(a).trim().toLowerCase() === String(b).trim().toLowerCase();
const isTeamSide = (side) => !NEUTRAL_SIDES.has(String(side || '').trim().toLowerCase());
const sideRank = (s) => {
  const i = SIDE_ORDER.indexOf(s);
  return i === -1 ? SIDE_ORDER.length : i;
};

export function buildMarkets(bets, { fixtureId, playerId = null, bookLogos = {}, teamName = null, teamSide = null }) {
  const groups = new Map();

  (bets || []).forEach((bet) => {
    if (!bet || bet.fixture_id !== fixtureId || bet.price == null) return;
    if (playerId ? bet.playerId !== playerId : !!bet.playerId) return;
    if (isPickemBook(bet.bookName)) return;

    const rawSide = bet.side || 'Over';

    // Team panel: keep only that team's side of spreads/moneylines, and only
    // that team's team-total. Game-wide markets (totals) stay whole.
    // NB the snapshot labels team sides 'Away'/'Home' (the alt grid from the
    // server uses team names), so this filters on the side, not the name.
    const teamOwner = !playerId && !isTeamSide(rawSide) ? (bet.selection || '') : '';
    if (!playerId) {
      if (teamSide && isTeamSide(rawSide) && rawSide !== teamSide) return;
      if (teamName && teamOwner && !sameTeam(teamOwner, teamName)) return;
    }

    // Team totals share one market id across both teams, so the team has to be
    // part of the key or the two teams' books collide on one row.
    const key = `${bet.market}||${bet.periodType || ''}||${teamOwner}`;
    if (!groups.has(key)) {
      groups.set(key, {
        market: teamOwner && !(teamName || teamSide)
          ? `${teamOwner} ${bet.marketName || bet.market}`
          : (bet.marketName || bet.market),
        market_id: bet.market,
        periodType: bet.periodType || '',
        teamOwner,
        sides: new Set(),
        books: new Map(),
      });
    }
    const g = groups.get(key);
    // Yes-only player markets (anytime/first/last TD scorer) aren't classified as
    // player props server-side, so their side comes through as Away/Home from the
    // team branch of transformRecord. For a player they only have one real side.
    let side = bet.side || 'Over';
    if (playerId && side !== 'Over' && side !== 'Under') side = 'Over';
    g.sides.add(side);

    // One entry per book; a book quotes one main line per market.
    if (!g.books.has(bet.bookName)) {
      g.books.set(bet.bookName, {
        book: bet.bookName,
        logo: bookLogos[bet.bookName] || '',
        line: bet.points == null ? null : Number(bet.points),
        prices: {},
      });
    }
    const b = g.books.get(bet.bookName);
    if (b.line == null && bet.points != null) b.line = Number(bet.points);
    b.prices[side] = Number(bet.price);
  });

  return [...groups.values()].map((g) => {
    let sides = [...g.sides].sort((a, b) => sideRank(a) - sideRank(b));
    // One-sided market → label it "Yes" rather than a misleading "Over"
    if (sides.length === 1 && sides[0] === 'Over') {
      sides = ['Yes'];
      g.books.forEach((b) => { b.prices.Yes = b.prices.Over; delete b.prices.Over; });
    }
    const books = [...g.books.values()].sort((a, b) => a.book.localeCompare(b.book));

    // Consensus line = the line most books are on. Best prices are compared
    // only among books at that line, so it stays apples-to-apples.
    const tally = new Map();
    books.forEach((b) => { if (b.line != null) tally.set(b.line, (tally.get(b.line) || 0) + 1); });
    let line = null;
    let topCount = 0;
    tally.forEach((n, ln) => { if (n > topCount) { topCount = n; line = ln; } });

    const atLine = books.filter((b) => (line == null ? b.line == null : b.line === line));
    const best = sides.map((side) => {
      const winner = atLine.reduce((acc, b) => (
        b.prices[side] != null && (acc == null || b.prices[side] > acc.prices[side]) ? b : acc
      ), null);
      return winner
        ? { side, price: winner.prices[side], book: winner.book, logo: winner.logo }
        : { side, price: null, book: null, logo: '' };
    });

    return {
      market: g.market,
      market_id: g.market_id,
      periodType: g.periodType,
      teamOwner: g.teamOwner,
      line,
      lineCount: topCount,
      sides,
      best,
      books,
      book_count: books.length,
    };
  }).sort((a, b) => String(a.market).localeCompare(String(b.market)));
}

// ══════════════════════════════════════════════════════════
//  HOOK — markets are computed by the caller, so there's no fetch here
// ══════════════════════════════════════════════════════════

export function useMarkets() {
  const [marketsOpen, setMarketsOpen] = useState(false);
  const [marketsData, setMarketsData] = useState([]);
  const [marketsMeta, setMarketsMeta] = useState(null);

  const openMarkets = useCallback((meta, markets) => {
    setMarketsMeta(meta || null);
    setMarketsData(markets || []);
    setMarketsOpen(true);
  }, []);

  const closeMarkets = useCallback(() => setMarketsOpen(false), []);

  return { marketsOpen, marketsData, marketsMeta, openMarkets, closeMarkets };
}

// ══════════════════════════════════════════════════════════
//  MODAL
// ══════════════════════════════════════════════════════════

const fmtEventStart = (ts) => {
  if (!ts) return '';
  return new Date(ts).toLocaleString('en-US', {
    month: 'numeric', day: 'numeric', year: '2-digit',
    hour: 'numeric', minute: '2-digit', hour12: true,
  }).replace(',', '');
};


// "Player Passing Touchdowns" → "Passing Touchdowns" (the player is in the header)
const shortMarket = (m) => String(m || '').replace(/^Player\s+/i, '');

const marketKey = (m) => (m ? `${m.market_id}||${m.periodType}||${m.teamOwner || ''}` : '');

// Right-hand pane: consensus line + best price per side, then the alt grid.
function MarketDetail({ market, meta, price }) {
  // No header strip here: the left panel already shows the market, line and
  // best prices for the selected row, so the space goes to the grid instead.
  return <AltGrid market={market} meta={meta} price={price} sides={market.sides} />;
}

const SELECT_STYLE = {
  width: '100%',
  backgroundColor: '#222',
  color: '#eee',
  border: '1px solid #333',
  borderRadius: 4,
  padding: '5px 8px',
  fontSize: '0.8rem',
  fontFamily: 'inherit',
};


// ══════════════════════════════════════════════════════════
//  ALT GRID — lines × books for one market. Alternates aren't ingested,
//  so this fetches them on demand (one cached request per market).
// ══════════════════════════════════════════════════════════

function AltGrid({ market, meta, price, sides }) {
  const [data, setData] = useState(null);
  // Callback ref on the main-line row: centre it in the scroll box when the grid
  // renders (and when the side or market changes), so it opens at the lines
  // people actually bet rather than the top of the ladder.
  const mainRowRef = useCallback((node) => {
    if (!node) return;
    const box = node.closest('[data-grid-scroll]');
    if (!box) return;
    box.scrollTop = Math.max(0, node.offsetTop - (box.clientHeight / 2) + (node.offsetHeight / 2));
  }, []);
  const [error, setError] = useState(false);
  const [side, setSide] = useState(sides[0] || 'Over');

  // Lineless markets (moneyline, anytime/first/last TD scorer) have no ladder to
  // fetch — the snapshot already holds every book's price, so build the one-row
  // grid locally and skip the round-trip.
  const hasLines = market.books.some((b) => b.line != null);
  const localData = hasLines ? null : {
    sides: market.sides,
    books: market.books.map((b) => b.book),
    rows: [{
      line: null,
      is_main: true,
      books: Object.fromEntries(market.books.map((b) => [
        b.book,
        Object.fromEntries(Object.entries(b.prices).map(([sd, p]) => [sd, { price: p, is_main: true }])),
      ])),
    }],
  };

  useEffect(() => {
    if (!hasLines) return undefined;
    let cancelled = false;
    setData(null); setError(false);
    const params = new URLSearchParams({
      fixture_id: meta?.fixture_id || '',
      market_id: market.market_id,
      player_id: meta?.player_id || '',
      sportsbooks: (meta?.books || market.books.map((b) => b.book)).filter((b) => !isPickemBook(b)).join(','),
    });
    fetch(`${ALT_ODDS_API_URL}?${params}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => { console.error('[ALT-ODDS] Fetch error:', e); if (!cancelled) setError(true); });
    return () => { cancelled = true; };
  }, [market.market_id, meta, hasLines]);   // eslint-disable-line react-hooks/exhaustive-deps

  const grid = localData || data;

  if (error) {
    return <Box sx={GRID_WRAP}><Typography sx={EMPTY_SX}>Could not load alternates</Typography></Box>;
  }
  if (!grid) {
    return <Box sx={{ ...GRID_WRAP, display: 'flex', justifyContent: 'center' }}>
      <CircularProgress size={18} sx={{ color: '#555' }} />
    </Box>;
  }

  // On a team panel, drop the other team's side so there's no Away/Home toggle.
  // Server data labels sides with team names; locally built grids came from
  // buildMarkets, which already filtered to the clicked team's side.
  const allSides = grid.sides?.length ? grid.sides : sides;
  const teamFiltered = meta?.teamName && !localData
    ? allSides.filter((sd) => !isTeamSide(sd) || sameTeam(sd, meta.teamName))
    : allSides;
  const gridSides = teamFiltered.length ? teamFiltered : allSides;
  const active = gridSides.includes(side) ? side : gridSides[0];
  const books = (grid.books || []).filter((b) => !isPickemBook(b));
  const rows = grid.rows || [];

  // Main lines are per book AND per side: on a spread, GB's main +4.5 must not
  // mark the 4.5 row while you're looking at ATL. Any book whose main line for
  // the active side is this row earns the dot; the most-agreed line is the one
  // the grid centres on.
  let centreLine = null;
  let centreVotes = 0;
  rows.forEach((row) => {
    let votes = 0;
    books.forEach((b) => { if (row.books?.[b]?.[active]?.is_main) votes += 1; });
    if (votes > centreVotes) { centreVotes = votes; centreLine = row.line; }
  });

  if (!rows.length || !books.length) {
    return <Box sx={GRID_WRAP}><Typography sx={EMPTY_SX}>No alternates offered</Typography></Box>;
  }

  return (
    <Box sx={GRID_WRAP}>
      {/* Side toggle */}
      {gridSides.length > 1 && (
        <Box sx={{ display: 'flex', gap: 0.5, mb: 0.8, flexShrink: 0 }}>
          {gridSides.map((sd) => (
            <Box key={sd} onClick={() => setSide(sd)} sx={{
              px: 1, py: 0.2, fontSize: '0.7rem', cursor: 'pointer', borderRadius: '3px',
              color: sd === active ? '#fff' : '#777',
              backgroundColor: sd === active ? '#2a2a2a' : 'transparent',
              '&:hover': { color: '#fff' },
            }}>{sd}</Box>
          ))}
        </Box>
      )}

      <Box sx={SCROLL_SX} data-grid-scroll>
        <table style={{ borderCollapse: 'collapse', width: 'max-content', minWidth: '100%' }}>
          <thead>
            <tr>
              <th style={{ ...GRID_TH, position: 'sticky', left: 0, zIndex: 3, textAlign: 'left', minWidth: 54 }}>Line</th>
              <th style={{ ...GRID_TH, position: 'sticky', left: 54, zIndex: 3, borderRight: '1px solid #2a2a2a', minWidth: 64 }}>Best</th>
              {books.map((b) => (
                <th key={b} style={GRID_TH} title={b}>
                  {meta?.bookLogos?.[b]
                    ? <img src={meta.bookLogos[b]} alt={b} style={LOGO_STYLE} />
                    : <span style={{ fontSize: '0.6rem' }}>{b.slice(0, 3)}</span>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              // Best price on this line, for highlighting
              let bestPrice = null;
              let bestBook = null;
              // "Main" is per side: on a spread, GB's main +4.5 must not mark the
              // 4.5 row as main while you're looking at ATL.
              let isMainRow = false;
              books.forEach((b) => { if (row.books?.[b]?.[active]?.is_main) isMainRow = true; });
              books.forEach((b) => {
                const cell = row.books?.[b]?.[active];
                const p = cell?.price;
                if (p != null && (bestPrice == null || p > bestPrice)) { bestPrice = p; bestBook = b; }
              });
              return (
                <tr key={row.line == null ? 'ml' : row.line}
                  ref={row.line === centreLine ? mainRowRef : null}
                  style={{ borderTop: '1px solid #1d1d1d' }}>
                  <td style={{
                    ...GRID_TD, ...NUM_STYLE, position: 'sticky', left: 0, zIndex: 2,
                    backgroundColor: '#151515', textAlign: 'left',
                    color: isMainRow ? '#fff' : '#999',
                  }}>
                    {row.line == null ? '—' : row.line}
                    {isMainRow && row.line != null ? <span style={{ color: '#ff1744' }}> •</span> : null}
                  </td>
                  <td style={{
                    ...GRID_TD, ...NUM_STYLE, position: 'sticky', left: 54, zIndex: 2,
                    backgroundColor: '#151515', borderRight: '1px solid #2a2a2a',
                    color: bestPrice == null ? '#3a3a3a' : '#fff', fontWeight: 600,
                  }} title={bestBook || ''}>
                    {bestPrice == null ? '·' : (
                      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4 }}>
                        {price(bestPrice)}
                        {meta?.bookLogos?.[bestBook] && (
                          <img src={meta.bookLogos[bestBook]} alt="" style={{ ...LOGO_STYLE, width: 12, height: 12 }} />
                        )}
                      </Box>
                    )}
                  </td>
                  {books.map((b) => {
                    const cell = row.books?.[b]?.[active];
                    const isBest = cell?.price != null && cell.price === bestPrice;
                    return (
                      <td key={b} style={{
                        ...GRID_TD, ...NUM_STYLE,
                        color: cell?.price == null ? '#3a3a3a' : (isBest ? '#fff' : '#aaa'),
                        fontWeight: isBest ? 600 : 400,
                        // Same best-odds treatment as the Odds/Props grids
                        boxShadow: isBest ? 'inset 0 0 0 2px #00cc00' : 'none',
                        borderRadius: isBest ? 3 : 0,
                      }}>
                        {cell?.price == null ? '·' : price(cell.price)}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </Box>
      <Typography sx={{ mt: 0.6, fontSize: '0.65rem', color: '#555', flexShrink: 0 }}>
        {hasLines ? '• main line · highlighted = best price on that line' : 'highlighted = best price'}
      </Typography>
    </Box>
  );
}

const GRID_WRAP = { backgroundColor: '#151515', px: 2, py: 1, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' };

// One scroll container for both axes, so the horizontal bar stays pinned to the
// bottom of the pane instead of the bottom of a very tall table. Chrome hides
// overlay scrollbars until you scroll, so they're styled to always show.
const SCROLL_SX = {
  flex: 1, minHeight: 0, overflow: 'auto',
  '&::-webkit-scrollbar': { width: 10, height: 10 },
  '&::-webkit-scrollbar-track': { backgroundColor: '#1a1a1a' },
  '&::-webkit-scrollbar-thumb': { backgroundColor: '#3a3a3a', borderRadius: 5 },
  '&::-webkit-scrollbar-thumb:hover': { backgroundColor: '#4a4a4a' },
  scrollbarWidth: 'thin',
  scrollbarColor: '#3a3a3a #1a1a1a',
};
const EMPTY_SX = { color: '#555', fontSize: '0.78rem', textAlign: 'center', py: 1 };
const GRID_TH = {
  position: 'sticky', top: 0, zIndex: 1, backgroundColor: '#151515',
  padding: '2px 5px', textAlign: 'center', fontWeight: 500,
  color: '#666', fontSize: '0.66rem', borderBottom: '1px solid #2a2a2a',
};
const GRID_TD = { padding: '2px 5px', textAlign: 'center', fontSize: '0.74rem' };

const LOGO_STYLE = { width: 16, height: 16, objectFit: 'contain', borderRadius: 2, flexShrink: 0 };

const NUM_SX = {
  fontSize: '0.82rem',
  fontVariantNumeric: 'tabular-nums',
  fontFamily: 'monospace',
  whiteSpace: 'nowrap',
};

export default function MarketsModal({ open, onClose, markets, meta }) {
  const oddsFormat = useOddsFormat();
  const rows = markets || [];
  const narrow = useMediaQuery('(max-width:820px)');
  const [selectedKey, setSelectedKey] = useState(null);

  // Default to the first market whenever a new player/game is opened.
  useEffect(() => {
    setSelectedKey(rows.length ? marketKey(rows[0]) : null);
  }, [meta, open]);   // eslint-disable-line react-hooks/exhaustive-deps

  if (!open) return null;

  const price = (p) => (p == null ? '-' : fmtPrice(p, oddsFormat));
  const selected = rows.find((m) => marketKey(m) === selectedKey) || rows[0] || null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: '#1a1a1a',
          color: '#ffffff',
          maxHeight: '85vh',
          maxWidth: narrow ? '560px' : 'min(1240px, 96vw)',
          borderRadius: '8px',
          border: '1px solid #333',
        },
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        {/* ── Header ── */}
        <Box sx={{ px: 2.5, pt: 2, pb: 1.5, borderBottom: '1px solid #2a2a2a' }}>
          <IconButton onClick={onClose} size="small"
            sx={{ position: 'absolute', top: 8, right: 8, color: '#555', '&:hover': { color: '#fff' } }}>
            <CloseIcon fontSize="small" />
          </IconButton>

          <Typography sx={{ fontSize: '0.95rem', color: '#fff', pr: 4 }}>
            {meta?.title || ''}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.3 }}>
            {meta?.league && (
              <>
                <Typography sx={{ fontSize: '0.75rem', color: '#888' }}>{meta.league}</Typography>
                <Typography sx={{ fontSize: '0.75rem', color: '#444', mx: 0.3 }}>|</Typography>
              </>
            )}
            {meta?.awayTeamLogo && (
              <img src={meta.awayTeamLogo} alt="" style={{ width: 14, height: 14, objectFit: 'contain' }} />
            )}
            <Typography sx={{ fontSize: '0.75rem', color: '#aaa' }}>{meta?.awayTeam}</Typography>
            <Typography sx={{ fontSize: '0.7rem', color: '#555' }}>@</Typography>
            {meta?.homeTeamLogo && (
              <img src={meta.homeTeamLogo} alt="" style={{ width: 14, height: 14, objectFit: 'contain' }} />
            )}
            <Typography sx={{ fontSize: '0.75rem', color: '#aaa' }}>{meta?.homeTeam}</Typography>
          </Box>

          {meta?.eventStart && (
            <Typography sx={{ fontSize: '0.72rem', color: '#666', mt: 0.3 }}>
              {fmtEventStart(meta.eventStart)}
            </Typography>
          )}
        </Box>

        {/* ── Content: market list on the left, alt grid on the right ── */}
        {rows.length === 0 ? (
          <Box sx={{ py: 5, textAlign: 'center' }}>
            <Typography sx={{ color: '#555', fontSize: '0.85rem' }}>No markets available</Typography>
          </Box>
        ) : narrow ? (
          <Box>
            {/* Narrow: market picker above the grid */}
            <Box sx={{ px: 2, py: 1, borderBottom: '1px solid #2a2a2a' }}>
              <select
                value={selectedKey || ''}
                onChange={(e) => setSelectedKey(e.target.value)}
                style={SELECT_STYLE}
              >
                {rows.map((m) => (
                  <option key={marketKey(m)} value={marketKey(m)}>
                    {shortMarket(m.market)}{m.line == null ? '' : ` ${m.line}`} — {m.book_count} book{m.book_count === 1 ? '' : 's'}
                  </option>
                ))}
              </select>
            </Box>
            {selected && <MarketDetail market={selected} meta={meta} price={price} />}
          </Box>
        ) : (
          <Box sx={{ display: 'flex', height: 'min(620px, 70vh)' }}>
            {/* Market list */}
            <Box sx={{ width: 196, flexShrink: 0, borderRight: '1px solid #2a2a2a', overflowY: 'auto' }}>
              {rows.map((m) => {
                const key = marketKey(m);
                const isSel = key === marketKey(selected);
                return (
                  <Box key={key} onClick={() => setSelectedKey(key)} sx={{
                    px: 1.5, py: 0.9, cursor: 'pointer',
                    borderBottom: '1px solid #202020',
                    borderLeft: isSel ? '2px solid #ff1744' : '2px solid transparent',
                    backgroundColor: isSel ? '#232323' : 'transparent',
                    '&:hover': { backgroundColor: isSel ? '#232323' : '#1f1f1f' },
                  }}>
                    <Typography sx={{
                      fontSize: '0.8rem', color: isSel ? '#fff' : '#ccc',
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }} title={m.market}>
                      {shortMarket(m.market)}
                    </Typography>
                    <Typography sx={{ fontSize: '0.68rem', color: '#666' }}>
                      {m.line == null ? '' : `${m.line} · `}
                      {m.book_count} book{m.book_count === 1 ? '' : 's'}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.9, mt: 0.2 }}>
                      {m.best.slice(0, 2).map((b) => (b.price == null ? null : (
                        <Box key={b.side} sx={{ display: 'flex', alignItems: 'center', gap: 0.35 }}
                          title={`${b.side} — ${b.book}`}>
                          <Typography sx={{ fontSize: '0.62rem', color: '#666' }}>{b.side.charAt(0)}</Typography>
                          <Typography sx={{ ...NUM_SX, fontSize: '0.7rem', color: '#bbb' }}>{price(b.price)}</Typography>
                          {b.logo && <img src={b.logo} alt="" style={{ ...LOGO_STYLE, width: 12, height: 12 }} />}
                        </Box>
                      )))}
                    </Box>
                  </Box>
                );
              })}
            </Box>

            {/* Selected market — header pinned, grid scrolls inside */}
            <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
              {selected && <MarketDetail market={selected} meta={meta} price={price} />}
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}

const SUB_TH = {
  textAlign: 'left',
  padding: '3px 6px',
  color: '#555',
  fontSize: '0.66rem',
  fontWeight: 500,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
};

const TD_STYLE = {
  padding: '3px 6px',
  fontSize: '0.78rem',
};

const NUM_STYLE = { fontVariantNumeric: 'tabular-nums', fontFamily: 'monospace', whiteSpace: 'nowrap' };